import React, { useState, useEffect } from "react";
import {
  Wallet,
  Loader2,
  Check,
  Plus,
  X,
  Gift,
} from "lucide-react";

import CommonButton from "../../CommonButton";
import { IWalletTopupPackage } from "../../../types/Response/CustomerResponse";

interface WalletCardProps {
  availableBalance: number;
  appliedAmount: number;
  maximumRedeemable: number;
  canRedeem: boolean;
  isApplied: boolean;
  onApply: (amountToApply: number) => void;
  onRemove: () => void;
  onAddMoney?: (
    amount: number,
    offerId?: number
  ) => Promise<void> | void;
  onOpenTopUpPortal?: () => void;
  topUpPackages?: IWalletTopupPackage[];
  isPackagesLoading?: boolean;
  isLoading?: boolean;
}

const WalletCard: React.FC<WalletCardProps> = ({
  availableBalance = 0,
  appliedAmount = 0,
  maximumRedeemable = 0,
  canRedeem = false,
  isApplied = false,
  onApply,
  onRemove,
  onAddMoney,
  onOpenTopUpPortal,
  topUpPackages = [],
  isPackagesLoading = false,
  isLoading = false,
}) => {
  const [isAddingMoney, setIsAddingMoney] =
    useState(false);

  const [addAmount, setAddAmount] =
    useState("");

  const [selectedOfferId, setSelectedOfferId] =
    useState<number | undefined>(undefined);

  const [isAddingLoading, setIsAddingLoading] =
    useState(false);

  // Custom redeem input
  const [customRedeemAmount, setCustomRedeemAmount] =
    useState<string>("");

  // Max cap calculation
  const maxAllowedToRedeem = Math.min(
    availableBalance,
    maximumRedeemable
  );

  // Auto-fill max redeemable
  useEffect(() => {
    if (
      !isApplied &&
      maxAllowedToRedeem > 0
    ) {
      setCustomRedeemAmount(
        maxAllowedToRedeem.toFixed(2)
      );
    }
  }, [
    maxAllowedToRedeem,
    isApplied,
  ]);

  /* =========================================================
     APPLY WALLET AMOUNT
  ========================================================= */
  const handleApplyClick = () => {
    const parsed =
      parseFloat(customRedeemAmount);

    if (
      isNaN(parsed) ||
      parsed <= 0
    ) {
      return;
    }

    const finalAmount = Math.min(
      parsed,
      maxAllowedToRedeem
    );

    onApply(finalAmount);
  };

  /* =========================================================
     ADD MONEY
  ========================================================= */
  const handleAddMoneySubmit =
    async () => {
      const parsedAmount =
        parseFloat(addAmount);

      if (
        isNaN(parsedAmount) ||
        parsedAmount <= 0 ||
        !onAddMoney
      ) {
        return;
      }

      try {
        setIsAddingLoading(true);

        await onAddMoney(
          parsedAmount,
          selectedOfferId
        );

        setAddAmount("");
        setSelectedOfferId(undefined);
        setIsAddingMoney(false);
      } catch (error) {
        console.error(error);
      } finally {
        setIsAddingLoading(false);
      }
    };

  /* =========================================================
     DYNAMIC REWARD MESSAGE
  ========================================================= */
  const getDynamicRewardMessage =
    () => {
      const numericAmount =
        parseFloat(addAmount);

      if (
        isNaN(numericAmount) ||
        numericAmount <= 0
      ) {
        return null;
      }

      const matchedPackage =
        topUpPackages.find(
          (p) =>
            p.topupAmount ===
            numericAmount
        );

      if (
        !matchedPackage ||
        matchedPackage.bonusAmount <= 0
      ) {
        return null;
      }

      return (
        <div
          className="
            mt-2
            max-[639px]:mt-1.5

            flex
            items-center

            gap-1.5
            max-[639px]:gap-1

            text-[11px]
            max-[639px]:text-[9px]

            font-bold
            text-emerald-600

            animate-in
            fade-in
            duration-100
          "
        >
          <Gift
            size={13}
            className="
              shrink-0

              max-[639px]:w-3
              max-[639px]:h-3
            "
          />

          <span>
            Awesome! You will get AED{" "}
            {matchedPackage.bonusAmount}{" "}
            bonus balance instantly!
          </span>
        </div>
      );
    };

  /* =========================================================
     SELECT PRESET
  ========================================================= */
  const handleSelectPreset = (
    pkg: IWalletTopupPackage
  ) => {
    setAddAmount(
      pkg.topupAmount.toString()
    );

    setSelectedOfferId(pkg.id);
  };

  const parsedCustom =
    parseFloat(customRedeemAmount);

  const isCustomValid =
    !isNaN(parsedCustom) &&
    parsedCustom > 0 &&
    parsedCustom <=
      maxAllowedToRedeem;

  return (
    <div
      className="
        w-full

        p-4
        max-[639px]:p-2.5

        bg-white

        border
        border-slate-100

        rounded-2xl

        shadow-md

        transition-all
        duration-300
      "
    >
      {/* =====================================================
          HEADER
      ===================================================== */}
      <div
        className="
          mb-3.5
          max-[639px]:mb-2.5

          flex
          items-center
          justify-between

          gap-2
        "
      >
        {/* LEFT */}
        <div
          className="
            flex
            items-center

            gap-2.5
            max-[639px]:gap-1.5

            min-w-0
          "
        >
          {/* WALLET ICON */}
          <div
            className="
              flex

              h-8
              w-8

              max-[639px]:h-6
              max-[639px]:w-6

              shrink-0

              items-center
              justify-center

              rounded-lg

              bg-gradient-to-br
              from-[#2F5E2E]
              to-emerald-600

              shadow-sm
            "
          >
            <Wallet
              size={14}
              className="
                text-white

                max-[639px]:w-3.5
                max-[639px]:h-3.5
              "
            />
          </div>

          {/* TITLE */}
          <div className="min-w-0">
            <h2
              className="
                text-sm
                max-[639px]:text-[11px]

                font-bold
                text-slate-800

                tracking-wide

                truncate
              "
            >
              Wallet Balance
            </h2>

            <p
              className="
                text-[11px]
                max-[639px]:text-[9px]

                text-slate-400

                truncate
              "
            >
              Pay securely using your wallet funds
            </p>
          </div>
        </div>

        {/* =================================================
            ADD MONEY / CANCEL
        ================================================= */}
        <button
          type="button"
          onClick={() => {
            const nextState =
              !isAddingMoney;

            setIsAddingMoney(
              nextState
            );

            setAddAmount("");
            setSelectedOfferId(
              undefined
            );

            if (
              nextState &&
              onOpenTopUpPortal
            ) {
              onOpenTopUpPortal();
            }
          }}
          className={`
            flex
            items-center

            gap-1

            px-2.5
            max-[639px]:px-1.5

            py-1
            max-[639px]:py-0.5

            rounded-lg

            text-xs
            max-[639px]:text-[9px]

            font-bold

            transition-all

            border

            shrink-0

            ${
              isAddingMoney
                ? "bg-slate-50 border-slate-200 text-slate-500"
                : "bg-emerald-50 border-emerald-100 text-[#2F5E2E] hover:bg-emerald-100"
            }
          `}
        >
          {isAddingMoney ? (
            <>
              <X
                size={12}
                className="
                  max-[639px]:w-3
                  max-[639px]:h-3
                "
              />
              Cancel
            </>
          ) : (
            <>
              <Plus
                size={12}
                className="
                  max-[639px]:w-3
                  max-[639px]:h-3
                "
              />
              Add Money
            </>
          )}
        </button>
      </div>

      {/* =====================================================
          MAIN VIEW
      ===================================================== */}
      {!isAddingMoney ? (
        <div className="space-y-2 max-[639px]:space-y-1.5">
          {/* =================================================
              BALANCE AREA
          ================================================= */}
          <div
            className={`
              flex
              items-center
              justify-between

              gap-3
              max-[639px]:gap-2

              border
              rounded-xl

              p-3
              max-[639px]:p-2

              ${
                isApplied
                  ? "bg-emerald-50/40 border-emerald-100"
                  : "bg-slate-50/50 border-slate-100"
              }
            `}
          >
            {/* LEFT */}
            <div
              className="
                space-y-1
                max-[639px]:space-y-0.5

                min-w-0
                flex-1
              "
            >
              {/* AVAILABLE */}
              <p
                className="
                  text-[10px]
                  max-[639px]:text-[8px]

                  font-bold

                  text-slate-400

                  uppercase
                  tracking-wider

                  truncate
                "
              >
                Available: AED{" "}
                {availableBalance.toFixed(2)}
              </p>

              {/* CUSTOM INPUT */}
              {!isApplied &&
              canRedeem &&
              maxAllowedToRedeem > 0 ? (
                <div
                  className="
                    flex
                    items-center

                    gap-1.5
                    max-[639px]:gap-1
                  "
                >
                  <span
                    className="
                      text-xs
                      max-[639px]:text-[9px]

                      font-bold
                      text-slate-700
                    "
                  >
                    AED
                  </span>

                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={
                      maxAllowedToRedeem
                    }
                    value={
                      customRedeemAmount
                    }
                    onChange={(e) =>
                      setCustomRedeemAmount(
                        e.target.value
                      )
                    }
                    placeholder={`Max ${maxAllowedToRedeem.toFixed(
                      2
                    )}`}
                    className="
                      w-24
                      max-[639px]:w-20

                      px-2
                      max-[639px]:px-1.5

                      py-1
                      max-[639px]:py-0.5

                      text-sm
                      max-[639px]:text-[10px]

                      font-extrabold

                      text-slate-800

                      bg-white

                      border
                      border-slate-300

                      rounded-lg

                      focus:outline-none
                      focus:border-[#2F5E2E]
                      focus:ring-1
                      focus:ring-[#2F5E2E]
                    "
                  />
                </div>
              ) : (
                <p
                  className="
                    text-sm
                    max-[639px]:text-[11px]

                    font-extrabold
                    text-slate-800
                  "
                >
                  {isApplied
                    ? `AED ${appliedAmount.toFixed(
                        2
                      )}`
                    : `AED ${availableBalance.toFixed(
                        2
                      )}`}
                </p>
              )}

              {/* MAX REDEEMABLE */}
              {canRedeem &&
                maxAllowedToRedeem > 0 &&
                !isApplied && (
                  <p
                    className="
                      text-[10px]
                      max-[639px]:text-[8px]

                      text-slate-400

                      font-medium
                    "
                  >
                    Max redeemable:{" "}
                    <span className="font-bold text-slate-600">
                      AED{" "}
                      {maxAllowedToRedeem.toFixed(
                        2
                      )}
                    </span>
                  </p>
                )}
            </div>

            {/* =================================================
                ACTION BUTTON
            ================================================= */}
            <div className="shrink-0">
              {isApplied ? (
                <button
                  type="button"
                  disabled={isLoading}
                  onClick={onRemove}
                  className="
                    text-xs
                    max-[639px]:text-[9px]

                    font-bold
                    text-red-500

                    bg-white

                    border
                    border-slate-200

                    rounded-lg

                    px-3.5
                    max-[639px]:px-2

                    py-1.5
                    max-[639px]:py-1

                    shadow-xs

                    transition

                    hover:bg-red-50
                    hover:border-red-200

                    active:scale-95

                    disabled:opacity-50
                  "
                >
                  {isLoading ? (
                    <Loader2
                      size={12}
                      className="
                        animate-spin

                        max-[639px]:w-3
                        max-[639px]:h-3
                      "
                    />
                  ) : (
                    "Remove"
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  disabled={
                    isLoading ||
                    !canRedeem ||
                    availableBalance <= 0 ||
                    !isCustomValid
                  }
                  onClick={
                    handleApplyClick
                  }
                  className="
                    text-xs
                    max-[639px]:text-[9px]

                    font-bold
                    text-white

                    bg-gradient-to-r
                    from-[#2F5E2E]
                    to-emerald-600

                    rounded-lg

                    px-4
                    max-[639px]:px-2.5

                    py-1.5
                    max-[639px]:py-1

                    shadow-xs

                    transition-all

                    active:scale-95

                    disabled:opacity-40
                  "
                >
                  {isLoading ? (
                    <Loader2
                      size={12}
                      className="
                        animate-spin

                        max-[639px]:w-3
                        max-[639px]:h-3
                      "
                    />
                  ) : (
                    "Apply"
                  )}
                </button>
              )}
            </div>
          </div>

          {/* =================================================
              APPLIED MESSAGE
          ================================================= */}
          {isApplied && (
            <p
              className="
                text-[11px]
                max-[639px]:text-[9px]

                text-emerald-800

                font-bold

                flex
                items-center

                gap-1.5
                max-[639px]:gap-1

                px-1
              "
            >
              <Check
                size={13}
                className="
                  text-emerald-600
                  stroke-[3]

                  max-[639px]:w-3
                  max-[639px]:h-3
                "
              />

              Applied{" "}
              <span
                className="
                  font-mono
                  font-extrabold

                  text-emerald-700

                  bg-emerald-100/60

                  px-1.5
                  max-[639px]:px-1

                  py-0.5

                  rounded
                "
              >
                AED{" "}
                {appliedAmount.toFixed(2)}
              </span>{" "}
              from wallet balance.
            </p>
          )}
        </div>
      ) : (
        /* =====================================================
           TOP-UP PANEL
        ===================================================== */
        <div
          className="
            rounded-xl

            border
            border-slate-100

            bg-slate-50/50

            p-3
            max-[639px]:p-2

            animate-in
            slide-in-from-top-2
            duration-200
          "
        >
          {/* =================================================
              AMOUNT INPUT
          ================================================= */}
          <div
            className="
              flex
              items-center

              gap-2
              max-[639px]:gap-1
            "
          >
            <div className="flex-1 min-w-0">
              <input
                type="number"
                value={addAmount}
                onChange={(e) => {
                  const val =
                    e.target.value;

                  setAddAmount(val);

                  const match =
                    topUpPackages.find(
                      (p) =>
                        p.topupAmount.toString() ===
                        val
                    );

                  setSelectedOfferId(
                    match?.id
                  );
                }}
                placeholder="Enter amount (AED)"
                className="
                  w-full

                  px-3
                  max-[639px]:px-2

                  py-1.5
                  max-[639px]:py-1

                  text-xs
                  max-[639px]:text-[9px]

                  text-slate-800

                  bg-white

                  border
                  border-slate-300

                  rounded-lg

                  focus:outline-none
                  focus:border-[#2F5E2E]
                "
              />
            </div>

            <CommonButton
              text={
                isAddingLoading
                  ? ""
                  : "Add Money"
              }
              icon={
                isAddingLoading ? (
                  <Loader2
                    size={14}
                    className="
                      animate-spin

                      max-[639px]:w-3
                      max-[639px]:h-3
                    "
                  />
                ) : undefined
              }
              onClick={
                handleAddMoneySubmit
              }
              disabled={
                isAddingLoading ||
                !addAmount ||
                parseFloat(addAmount) <=
                  0
              }
              className="
                h-8
                max-[639px]:h-7

                px-4
                max-[639px]:px-2.5

                bg-[#2F5E2E]

                text-white

                font-bold

                text-xs
                max-[639px]:text-[9px]

                rounded-lg

                shadow-xs

                transition

                hover:bg-emerald-700

                disabled:opacity-40

                shrink-0
              "
            />
          </div>

          {/* =================================================
              LOADING PACKAGES
          ================================================= */}
          {isPackagesLoading ? (
            <div
              className="
                mt-4
                max-[639px]:mt-2.5

                flex
                items-center
                justify-center

                gap-2

                py-2
              "
            >
              <Loader2
                size={14}
                className="
                  animate-spin
                  text-[#2F5E2E]

                  max-[639px]:w-3
                  max-[639px]:h-3
                "
              />

              <span
                className="
                  text-[11px]
                  max-[639px]:text-[9px]

                  text-slate-400
                  font-medium
                "
              >
                Fetching active balance offers...
              </span>
            </div>
          ) : (
            topUpPackages.length >
              0 && (
                <div
                  className="
                    mt-3
                    max-[639px]:mt-2

                    grid
                    grid-cols-2
                    sm:grid-cols-4

                    gap-2
                    max-[639px]:gap-1.5
                  "
                >
                  {topUpPackages.map(
                    (pkg) => (
                      <div
                        key={pkg.id}
                        className="
                          flex
                          flex-col
                          items-center

                          gap-1
                          max-[639px]:gap-0.5
                        "
                      >
                        <button
                          type="button"
                          onClick={() =>
                            handleSelectPreset(
                              pkg
                            )
                          }
                          className={`
                            w-full

                            py-1.5
                            max-[639px]:py-1

                            px-1

                            bg-white

                            border
                            rounded-lg

                            text-[11px]
                            max-[639px]:text-[9px]

                            font-bold

                            transition

                            text-center

                            truncate

                            ${
                              addAmount ===
                              pkg.topupAmount.toString()
                                ? "border-[#2F5E2E] text-[#2F5E2E] bg-emerald-50/20"
                                : "border-slate-200 text-slate-600 hover:border-emerald-500"
                            }
                          `}
                        >
                          AED{" "}
                          {pkg.topupAmount}
                        </button>

                        {pkg.bonusAmount >
                          0 && (
                          <span
                            className="
                              text-[10px]
                              max-[639px]:text-[8px]

                              text-emerald-600

                              font-bold

                              whitespace-nowrap
                            "
                          >
                            +AED{" "}
                            {
                              pkg.bonusAmount
                            }{" "}
                            Free
                          </span>
                        )}
                      </div>
                    )
                  )}
                </div>
              )
          )}

          {/* =================================================
              DYNAMIC MESSAGE
          ================================================= */}
          {addAmount &&
            getDynamicRewardMessage()}
        </div>
      )}
    </div>
  );
};

export default WalletCard;